// The maker's garage: a small workshop around the car and the driver.
// Tiled floor, plank walls over a brick base, shelving with
// boxes and cans, a tool board, posters, and warm work lights.
import * as THREE from 'three'

function canvasTexture(w: number, h: number, draw: (x: CanvasRenderingContext2D) => void, repeat: [number, number] = [1, 1]) {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  draw(canvas.getContext('2d')!)
  const t = new THREE.CanvasTexture(canvas)
  t.colorSpace = THREE.SRGBColorSpace
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.repeat.set(...repeat)
  t.anisotropy = 8
  return t
}

const floorTexture = () => canvasTexture(512, 512, (x) => {
  x.fillStyle = '#8d9196'
  x.fillRect(0, 0, 512, 512)
  for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
    x.fillStyle = (i + j) % 2 ? '#959aa0' : '#878b91'
    x.fillRect(i * 128 + 2, j * 128 + 2, 124, 124)
  }
}, [3, 3])

const plankTexture = () => canvasTexture(512, 512, (x) => {
  x.fillStyle = '#c9c2b4'
  x.fillRect(0, 0, 512, 512)
  for (let i = 0; i < 512; i += 64) {
    x.fillStyle = i % 128 ? '#bdb6a7' : '#d3ccbe'
    x.fillRect(i, 0, 62, 512)
    x.fillStyle = 'rgba(80,60,40,0.25)'
    x.fillRect(i + 62, 0, 2, 512)
  }
  for (let k = 0; k < 120; k++) {
    x.fillStyle = 'rgba(120,100,80,0.12)'
    x.fillRect(Math.random() * 512, Math.random() * 512, 1, 30 + Math.random() * 60)
  }
}, [4, 1])

const brickTexture = () => canvasTexture(512, 256, (x) => {
  x.fillStyle = '#d9d6cf'
  x.fillRect(0, 0, 512, 256)
  for (let r = 0; r < 8; r++) for (let c = -1; c < 9; c++) {
    x.fillStyle = (r + c) % 3 ? '#ebe8e2' : '#e2ded6'
    x.fillRect(c * 64 + (r % 2) * 32 + 2, r * 32 + 2, 60, 28)
  }
}, [6, 1])

export type GarageRoom = { room: THREE.Group; setScreenshot: (i: number, url: string | null) => void }

export function buildGarageRoom(scene: THREE.Scene, opts: { screenshots?: (string | null)[] } = {}): GarageRoom {
  const room = new THREE.Group()
  const W = 14
  const D = 10
  const H = 5
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), new THREE.MeshStandardMaterial({ map: floorTexture(), roughness: 0.55, metalness: 0.05 }))
  floor.rotation.x = -Math.PI / 2
  floor.receiveShadow = true
  room.add(floor)

  const plank = new THREE.MeshStandardMaterial({ map: plankTexture(), roughness: 0.8 })
  const brick = new THREE.MeshStandardMaterial({ map: brickTexture(), roughness: 0.9 })
  const wall = (w: number, x: number, z: number, ry: number) => {
    const upper = new THREE.Mesh(new THREE.PlaneGeometry(w, H - 1.2), plank)
    upper.position.set(x, 1.2 + (H - 1.2) / 2, z)
    upper.rotation.y = ry
    const lower = new THREE.Mesh(new THREE.PlaneGeometry(w, 1.2), brick)
    lower.position.set(x, 0.6, z)
    lower.rotation.y = ry
    upper.receiveShadow = lower.receiveShadow = true
    room.add(upper, lower)
  }
  wall(W, 0, -D / 2, 0)
  wall(D, -W / 2, 0, Math.PI / 2)
  wall(D, W / 2, 0, -Math.PI / 2)
  // Timber posts and roof beams.
  const timber = new THREE.MeshStandardMaterial({ color: 0x8a6a48, roughness: 0.8 })
  for (const x of [-W / 2 + 0.2, -2.3, 2.3, W / 2 - 0.2]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, H, 0.3), timber)
    post.position.set(x, H / 2, -D / 2 + 0.2)
    room.add(post)
  }
  for (const z of [-3, 0, 3]) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(W, 0.3, 0.25), timber)
    beam.position.set(0, H - 0.2, z)
    room.add(beam)
  }

  // Shelving with boxes and cans.
  const wood = new THREE.MeshStandardMaterial({ color: 0x9a7650, roughness: 0.8 })
  const cardboard = new THREE.MeshStandardMaterial({ color: 0xb98f5c, roughness: 0.9 })
  const tin = new THREE.MeshStandardMaterial({ color: 0x9aa0a6, roughness: 0.3, metalness: 0.8 })
  const shelf = new THREE.Group()
  for (const y of [0.4, 1.3, 2.2, 3.1]) {
    const board = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.06, 0.6), wood)
    board.position.set(0, y, 0)
    shelf.add(board)
  }
  for (const x of [-1.75, 1.75]) for (const z of [-0.27, 0.27]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.06, 3.3, 0.06), wood)
    leg.position.set(x, 1.65, z)
    shelf.add(leg)
  }
  for (const [x, y] of [[-1.2, 1.55], [-0.5, 1.55], [0.6, 2.45], [1.2, 0.65], [-1.1, 0.65]]) {
    const box = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, 0.45), cardboard)
    box.position.set(x, y, 0)
    box.castShadow = true
    shelf.add(box)
  }
  for (const x of [-0.9, -0.6, -0.3, 0.9]) {
    const can = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.22, 16), tin)
    can.position.set(x, 3.25, 0)
    shelf.add(can)
  }
  shelf.position.set(W / 2 - 0.4, 0, -2.2)
  shelf.rotation.y = -Math.PI / 2
  room.add(shelf)

  // Posters on the left wall; the back wall is for the maker's screenshots.
  const poster = (text: string, color: string, z: number) => {
    const t = canvasTexture(256, 360, (c) => {
      c.fillStyle = color
      c.fillRect(0, 0, 256, 360)
      c.fillStyle = '#F7F7F5'
      c.font = 'italic 800 54px "Saira Condensed", "Arial Narrow", sans-serif'
      c.textAlign = 'center'
      c.fillText(text, 128, 200)
      c.font = '600 22px "Saira Condensed", "Arial Narrow", sans-serif'
      c.fillText('LAUNCHGP', 128, 320)
    })
    const p = new THREE.Mesh(new THREE.PlaneGeometry(0.75, 1.05), new THREE.MeshStandardMaterial({ map: t, roughness: 0.7 }))
    p.position.set(-W / 2 + 0.02, 3.2, z)
    p.rotation.y = Math.PI / 2
    room.add(p)
  }
  poster('GRAND PRIX', '#FF5A1F', -2.6)
  poster('ENDURANCE', '#4C9AFF', -1.5)

  // Four framed screenshots along the back wall (16:9), a bare board where
  // the maker hasn't hung one yet. setScreenshot swaps one in at once.
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x1c1d20, roughness: 0.6 })
  const loader = new THREE.TextureLoader()
  const placeholder = (n: number) => canvasTexture(640, 360, (c) => {
    c.fillStyle = '#e4dccb'
    c.fillRect(0, 0, 640, 360)
    c.fillStyle = '#9a9388'
    c.font = 'italic 800 48px "Saira Condensed", "Arial Narrow", sans-serif'
    c.textAlign = 'center'
    c.fillText(`WALL ${n + 1}`, 320, 170)
    c.font = '600 22px "Saira Condensed", "Arial Narrow", sans-serif'
    c.fillText('HANG A SCREENSHOT IN THE GARAGE FORM', 320, 220)
  })
  const shots: THREE.Mesh[] = []
  const FW = 2.0
  const FH = FW * 9 / 16
  ;[-4.55, -1.15, 1.15, 4.55].forEach((x, i) => {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(FW + 0.12, FH + 0.12, 0.06), frameMat)
    frame.position.set(x, 2.9, -D / 2 + 0.04)
    const shot = new THREE.Mesh(new THREE.PlaneGeometry(FW, FH), new THREE.MeshStandardMaterial({ map: placeholder(i), roughness: 0.8 }))
    shot.position.set(x, 2.9, -D / 2 + 0.075)
    room.add(frame, shot)
    shots.push(shot)
  })
  const setScreenshot = (i: number, url: string | null) => {
    const material = shots[i]?.material as THREE.MeshStandardMaterial | undefined
    if (!material) return
    if (!url) {
      material.map = placeholder(i)
      material.needsUpdate = true
      return
    }
    loader.load(url, (t) => {
      t.colorSpace = THREE.SRGBColorSpace
      t.anisotropy = 8
      material.map = t
      material.needsUpdate = true
    })
  }
  opts.screenshots?.forEach((url, i) => url && setScreenshot(i, url))

  // Tyre stack and a toolbox.
  const rubber = new THREE.MeshStandardMaterial({ color: 0x18181a, roughness: 0.9 })
  for (let i = 0; i < 4; i++) {
    const tyre = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.13, 10, 24), rubber)
    tyre.rotation.x = Math.PI / 2
    tyre.position.set(-5.6, 0.13 + i * 0.26, -3.4)
    tyre.castShadow = true
    room.add(tyre)
  }
  const toolbox = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.0, 0.5), new THREE.MeshStandardMaterial({ color: 0xc8312a, roughness: 0.35, metalness: 0.3 }))
  toolbox.position.set(-5.6, 0.5, -1.6)
  toolbox.castShadow = true
  room.add(toolbox)

  // Warm work lights.
  for (const x of [-3, 3]) {
    const lamp = new THREE.PointLight(0xffe2b8, 18, 14, 1.6)
    lamp.position.set(x, H - 0.6, 0)
    room.add(lamp)
    const shade = new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.3, 16, 1, true), new THREE.MeshStandardMaterial({ color: 0x2b2e33, side: THREE.DoubleSide }))
    shade.position.set(x, H - 0.45, 0)
    room.add(shade)
  }
  scene.add(room)
  return { room, setScreenshot }
}
